import React from "react";
import { Drawer, Schema, Icon, IconButton } from "rsuite";
import styled from "styled-components";


import { servicePick, removeTZFromDate, toUTCDateTime, } from "utils";
import { withTranslation } from "hoc/WithTranslation";
import { useWindowWidth } from "hooks";
import {
    LS_DIALER_CDR_COLUMNS,
    LS_DIALER_CDR_FILTERS,
    LS_DIALER_CDR_SMS_PER_PAGE,
    LS_DIALER_CDR_VOICE_PER_PAGE
} from "const/localStorageKeys";
import {
    LIMITED_PER_PAGE_DIALER,
    MIN_PER_PAGE_DIALER
} from "const";
import ButtonDownload from "components/client/ButtonDownload";
import { ButtonPrimary } from "components/base/BaseButton";
import { Spacer } from "components/base/Spacer";
import m from "definedMessages";
import {
    FilterSectionFlexboxGrid,
    StyledItemFitWidth,
    StyledItemAutoMargin
} from "components/base/FitContainers";
import CDRTable from "./CDRTable";
import CDRFilters from "./CDRFilters";
import useReportPage from "../useReportPage";

const { StringType } = Schema.Types;
const MAX_CHARACTERS = 40;

const calcActiveFiltersCDR = (filter, service) => {
    let active = 0;

    if (service) {
        active = filter?.a_number ? active + 1 : active;
        active = filter?.b_number ? active + 1 : active;
    } else {
        active = filter?.senderid ? active + 1 : active;
        active = filter?.phone ? active + 1 : active;
    }

    return active + 1;
};

const makeCDRFiltersModel = (formatMessage) => {
    return Schema.Model({
        a_number: StringType()
            .pattern(/^\d+$/, formatMessage(m.correctNumber))
            .maxLength(MAX_CHARACTERS, formatMessage(m.maxLength, { count: MAX_CHARACTERS })),
        b_number: StringType()
            .pattern(/^\d+$/, formatMessage(m.correctNumber))
            .maxLength(MAX_CHARACTERS, formatMessage(m.maxLength, { count: MAX_CHARACTERS }))
    });
};

const CDRPage = ({
    type,
    page,
    count,
    loading,
    per_page,
    columns,
    service,
    summaries,
    cdr_list,
    dateTimeState,
    uploadLoading,
    globalSettings,

    exportCDR: exportCDRProp,
    getCDRList,
    setDateTimeState,

    formatMessage
}) => {
    const resizedWidth = useWindowWidth();
    const isMobile = resizedWidth < 768;
    const perPageLSKey = service === false
        ? LS_DIALER_CDR_SMS_PER_PAGE
        : LS_DIALER_CDR_VOICE_PER_PAGE;

    const DEFAULT_COLUMNS_KEYS = ["range", "a_number", "senderid", "b_number", "phone"];

    const buildDefaultFilter = (svc, range, saved) => {
        if (saved) return { ...saved };

        const base = {
            range,
            start_date: removeTZFromDate(range[0]),
            end_date: removeTZFromDate(range[1])
        };

        if (svc) {
            return {
                ...base,
                a_number: "",
                b_number: ""
            };
        }

        return {
            ...base,
            senderid: "",
            phone: ""
        };
    };

    const adaptFilterByService = (svc, f) => {
        if (!f) return f;

        const next = { ...f };

        const renameField = (obj, from, to) => {
            if (Object.prototype.hasOwnProperty.call(obj, from)) {
                obj[to] = obj[from];
                delete obj[from];
            }
        };

        if (svc === true) {
            renameField(next, "senderid", "a_number");
            renameField(next, "phone", "b_number");

            if (!Object.prototype.hasOwnProperty.call(next, "a_number")) {
                next.a_number = "";
            }
            if (!Object.prototype.hasOwnProperty.call(next, "b_number")) {
                next.b_number = "";
            }
        } else if (svc === false) {
            renameField(next, "a_number", "senderid");
            renameField(next, "b_number", "phone");

            if (!Object.prototype.hasOwnProperty.call(next, "senderid")) {
                next.senderid = "";
            }
            if (!Object.prototype.hasOwnProperty.call(next, "phone")) {
                next.phone = "";
            }
        }

        return next;
    };

    const filtersModel = makeCDRFiltersModel(formatMessage);

    const validateFilter = (f) => {
        const checker = filtersModel.check(f || {});
        return Object.keys(checker).some((k) => checker[k].hasError);
    };

    const composeSummaryRow = (templateRow, s) => {
        const row = { ...templateRow };

        Object.keys(row).forEach((key) => {
            switch (key) {
                case "dialer_rate":
                    row[key] = servicePick(service, s?.rate, s?.cost) || null;
                    row["summary"] = true;
                    break;
                case "cost_dialer":
                    row[key] = s?.cost || null;
                    row["summary"] = true;
                    break;
                case "dialer_cur_name":
                    row[key] = s?.cur_name || null;
                    row["summary"] = true;
                    break;
                case "id":
                    row[key] = "summaries";
                    break;
                default:
                    row[key] = s?.[key] ?? null;
                    row["summary"] = true;
            }
        });

        return row;
    };

    const cdrConfig = {
        storageKeys: {
            perPage: perPageLSKey,
            columns: LS_DIALER_CDR_COLUMNS,
            filters: LS_DIALER_CDR_FILTERS
        },
        limits: {
            minPerPage: MIN_PER_PAGE_DIALER,
            maxPerPage: LIMITED_PER_PAGE_DIALER
        },
        columnsAll: columns,
        defaultColumnsKeys: DEFAULT_COLUMNS_KEYS,
        visibleKeysForService: (svc) =>
            svc
                ? ["range", "a_number", "b_number"]
                : ["range", "senderid", "phone"],

        buildDefaultFilter,
        adaptFilterByService,
        validateFilter,
        composeSummaryRow,

        fetchList: getCDRList,
        exportReport: exportCDRProp,
        toUTC: toUTCDateTime,

        service,
        type,
        perPageProp: per_page,
        dateTimeState,
        dataList: cdr_list,
        summaries,

        calcActiveFilters: (filter) => calcActiveFiltersCDR(filter, service)
    };

    const { state, actions } = useReportPage(cdrConfig);

    const {
        filter,
        shownColumns,
        perPage,
        savedTableColumns,
        activeFiltersCount,
        showFilters,
        dataWithSummaries
    } = state;

    const {
        setShowFilters,
        onChangeFilters,
        getItems,
        onSaveColumnsTable,
        applyFilterAndFetch,
        exportNow
    } = actions;

    return (
        <>
            {isMobile && (
                <>
                    <ButtonApplyFilterMobile onClick={() => applyFilterAndFetch(filter, 1)}>
                        {formatMessage(m.applyFilter)}
                    </ButtonApplyFilterMobile>

                    <Toggler
                        icon={<Icon icon="filter" />}
                        onClick={() => setShowFilters(true)}
                    >
                        {activeFiltersCount}
                    </Toggler>

                    <Drawer
                        size="xs"
                        placement="bottom"
                        style={{ height: "260px" }}
                        show={showFilters}
                        onHide={() => setShowFilters(false)}
                    >
                        <Drawer.Header />
                        <DrawerBody>
                            <CDRFilters
                                onChangeFilters={onChangeFilters}
                                loading={loading}
                                filtersModel={filtersModel}
                                {...{
                                    service,
                                    savedTableFilter: filter,
                                    globalSettings,
                                    dateTimeState,
                                    setDateTimeState
                                }}
                                mobile
                            />
                        </DrawerBody>
                    </Drawer>
                </>
            )}

            <Spacer size={30} />

            {!isMobile && (
                <>
                    <FilterSectionFlexboxGrid>
                        <StyledItemFitWidth>
                            <CDRFilters
                                onChangeFilters={onChangeFilters}
                                loading={loading}
                                filtersModel={filtersModel}
                                {...{
                                    service,
                                    savedTableFilter: filter,
                                    globalSettings,
                                    dateTimeState,
                                    setDateTimeState
                                }}
                            />
                        </StyledItemFitWidth>

                        <StyledItemAutoMargin>
                            <ButtonApplyFilter onClick={() => applyFilterAndFetch(filter, 1)}>
                                {formatMessage(m.applyFilter)}
                            </ButtonApplyFilter>

                            <ButtonDownload
                                onDownload={() => exportNow(page)}
                                loading={uploadLoading}
                                limitWidth={875}
                                buttonText={formatMessage(m.exportReport)}
                            />
                        </StyledItemAutoMargin>
                    </FilterSectionFlexboxGrid>

                    <Spacer size={19} />
                </>
            )}

            <Spacer size={1} />

            <CDRTable
                data={dataWithSummaries || cdr_list}
                extraRows={(dataWithSummaries && dataWithSummaries.length > (cdr_list?.length || 0)) ? 1 : 0}
                availableColumns={columns}
                pickedColumns={shownColumns}
                loading={loading}
                {...{
                    getItems,
                    page,
                    perPage,
                    perPageLSKey,
                    count,
                    more: undefined,
                    service,
                    savedTableColumns,
                    onSaveColumnsTable
                }}
            />

            {isMobile && (
                <StyledActionButtonWrapper>
                    <ButtonDownload
                        onDownload={() => exportNow(page)}
                        loading={uploadLoading}
                        limitWidth={875}
                        buttonText={formatMessage(m.exportReport)}
                    />
                </StyledActionButtonWrapper>
            )}
        </>
    );
};

export default withTranslation(CDRPage);

const Toggler = styled(IconButton)`
    && {
        color: #363434;
        float: right;
        margin-top: 6px;
    }
`;

const ButtonApplyFilter = styled(ButtonPrimary)`
    && {
        margin-right: 20px;
    }
`;

const StyledActionButtonWrapper = styled.div`
    padding-top: 15px;
    text-align: center;

    @media (min-width: 768px) {
        text-align: right;
    }
`;

const DrawerBody = styled(Drawer.Body)`
    && {
        position: initial;
    }

    && .dateTimePicker {
        height: 40px;
    }

    && .dateTimePicker.dateFrom {
        margin-top: -485px;
        position: absolute;
    }

    && .dateTimePicker.dateTo {
        margin-top: -165px;
        position: absolute;
    }

    && .dateTimePicker .dateTimeName {
        color: var(--color-light);
        font-weight: 600;
        background-color: rgba(0, 0, 0, 0.4);
        padding: 10px;
    }
`;

const ButtonApplyFilterMobile = styled(ButtonPrimary)`
    && {
        position: absolute;
        right: 100px;
    }
`;
